import { useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type { BatchAppliedDTO, BatchDetailDTO, BatchRowKind, IngestJobDTO } from '@contracts/ingest';
import { ApiClientError } from '../../../api/client';
import { notify } from '../../../lib/toast';
import { useAssistant } from '../AssistantProvider';
import { refreshKinds, watchType } from '../refresh';
import { applyBatch, batchKey, cancelBatch, type runBatch } from './api';
import { batchTitle } from './labels';
import { settleWrites } from './useRowWriter';

const fmt = (n: number) => n.toLocaleString('vi-VN');
/** The states `cancel` takes (the server's `cancelJob`). */
const CANCELLABLE: ReadonlySet<IngestJobDTO['state']> = new Set(['queued', 'reading', 'proposing']);

/** §8.5: the kinds a batch wrote, for the pages to refresh after the write and after its undo. */
export const kindsOf = (job: IngestJobDTO): BatchRowKind[] =>
  (Object.keys(job.counts.byKind) as BatchRowKind[]).filter((k) => (job.counts.byKind[k] ?? 0) > 0);
/** "Đã ghi N bản ghi từ <title>": the applied card's line and the undo toast's words. */
export const appliedLine = (job: IngestJobDTO) => `Đã ghi ${fmt(job.counts.included)} bản ghi từ ${batchTitle(job)}`;

/**
 * The jobs a refused Duyệt said were over (BATCH_EXPIRED), per query client: the card and the page of one job agree
 * on it whichever of them was refused, and a test's fresh client starts with none.
 */
const expiredByClient = new WeakMap<QueryClient, Set<Id>>();
const expiredOf = (client: QueryClient): Set<Id> => {
  let set = expiredByClient.get(client);
  if (set === undefined) expiredByClient.set(client, (set = new Set()));
  return set;
};

/**
 * The writes of one batch (§8.1, §8.2, §8.5), shared by its card in the panel and its own page: one request id per
 * intent kept across a network retry of the same click, one write at a time (`busy`), Duyệt after the rows' writes
 * settle, the rows a refused Duyệt named (BATCH_CHANGED), and the job's own undo.
 */
export function useBatchWrites(jobId: Id) {
  const { hanaUndo, glow } = useAssistant();
  const queryClient = useQueryClient();
  const [, setExpired] = useState(false);
  const expired = expiredOf(queryClient).has(jobId);
  // The rows a refused Duyệt named (BATCH_CHANGED), marked until the next Duyệt.
  const [changed, setChanged] = useState<ReadonlyMap<Id, string[]>>(() => new Map());
  const intents = useRef(new Map<string, string>());
  const [busy, setBusy] = useState(false);

  const write = <T,>(intent: string, call: (requestId: string) => Promise<T>, done: (value: T) => void,
    refused?: (e: ApiClientError) => boolean) => {
    if (busy) return;
    setBusy(true);
    let requestId = intents.current.get(intent);
    if (requestId === undefined) {
      requestId = crypto.randomUUID();
      intents.current.set(intent, requestId);
    }
    call(requestId).then((value) => {
      intents.current.delete(intent);
      done(value);
    }, (e: unknown) => {
      if (e instanceof ApiClientError && e.status !== 0) intents.current.delete(intent);
      if (!(e instanceof ApiClientError && refused?.(e) === true)) {
        notify.error(e instanceof ApiClientError && e.message !== '' ? e.message : 'Không thực hiện được. Hãy thử lại.');
      }
      void queryClient.invalidateQueries({ queryKey: batchKey(jobId) });
    }).finally(() => setBusy(false));
  };
  /** The job a write answered, into the cache the card and the page read. */
  const show = (next: IngestJobDTO) => {
    queryClient.setQueryData<BatchDetailDTO>(batchKey(jobId),
      (old) => ({ job: next, rows: old?.rows ?? { items: [], nextCursor: null } }));
  };
  const verb = (name: string, call: typeof runBatch, j: IngestJobDTO) =>
    write(name, (rid) => call(j.id, j.version, rid), show);
  /**
   * Hủy (P23): the worker moves a reading job on by itself (reading → proposing, a unit done), each step a new
   * version, so the version the card read can be one step old by the click. A conflict reads the job again and,
   * while it still reads, sends the cancel once more with the version now held; anything else is refused as before.
   */
  const cancel = (j: IngestJobDTO) => write('cancel', async (rid) => {
    try {
      return await cancelBatch(j.id, j.version, rid);
    } catch (e) {
      if (!(e instanceof ApiClientError && e.code === 'VERSION_CONFLICT')) throw e;
      await queryClient.refetchQueries({ queryKey: batchKey(jobId), exact: true });
      const fresh = queryClient.getQueryData<BatchDetailDTO>(batchKey(jobId))?.job;
      if (fresh === undefined || fresh.version === j.version || !CANCELLABLE.has(fresh.state)) throw e;
      return cancelBatch(j.id, fresh.version, crypto.randomUUID());
    }
  }, show);

  /**
   * Duyệt (§8.5): what the rows still hold is sent first, then the job is read again, so the apply carries the version
   * after the last edit and the person's last check. The answer is the applied job and its one undo: the toast and
   * ⌘Z (`hanaUndo.offer`), and the card's own Hoàn tác; the cards it wrote glow (§10.1, D7).
   */
  const apply = () => write('apply', async (rid): Promise<BatchAppliedDTO | null> => {
    await settleWrites(jobId);
    await queryClient.refetchQueries({ queryKey: batchKey(jobId), exact: true });
    const fresh = queryClient.getQueryData<BatchDetailDTO>(batchKey(jobId))?.job;
    if (fresh === undefined || fresh.state !== 'ready' || fresh.counts.included === 0) return null;
    return applyBatch(jobId, fresh.version, rid);
  }, (out) => {
    if (out === null) return;
    setChanged(new Map());
    show(out.job);
    const kinds = kindsOf(out.job);
    // D7: the cards it wrote glow where they are drawn, once the pages read them (the Map's slide in from the ghosts).
    void refreshKinds(queryClient, kinds).then(() => glow(out.objects));
    if (out.undoExpiresAt !== null) {
      hanaUndo.offer({ actionId: out.actionId, groupId: out.actionId, count: 1, summary: appliedLine(out.job),
        expiresAt: out.undoExpiresAt }, kinds.map((k) => ({ type: watchType(k), id: '' })));
    }
  }, (e) => {
    if (e.code === 'BATCH_CHANGED') {
      setChanged(new Map((e.extra?.rows ?? []).map((r) => [r.id, r.reasons])));
      notify.warning(e.message);
      return true;
    }
    if (e.code === 'BATCH_EXPIRED') {
      expiredOf(queryClient).add(jobId);                  // the card itself says it: "Lô này đã hết hạn"
      setExpired(true);
      return true;
    }
    return false;
  });
  const undo = (actionId: Id, j: IngestJobDTO) => {
    void hanaUndo.undo({ actionId, groupId: actionId }).then((r) => {
      if (r === 'undone') void refreshKinds(queryClient, kindsOf(j));
    });
  };

  return { busy, write, show, verb, cancel, apply, undo, changed, expired };
}
